import { useUserStore } from '@/store/user'
import { Socket } from 'socket.io-client'
import { computed, inject, onUnmounted, watch } from 'vue'
import { useRoute } from 'vue-router'

const useWorkshopRoom = (): void => {
  const socket = inject<Socket>('socket')
  const route = useRoute()
  const userStore = useUserStore()

  // What we have asked the server for, and what it has confirmed. They differ
  // while a subscribe is in flight, and stay apart when one is refused.
  let requestedWorkshopId: string | null = null
  let joinedWorkshopId: string | null = null

  const currentWorkshopId = computed(() =>
    typeof route.params.uuid === 'string' ? route.params.uuid : null
  )

  const leaveCurrentRoom = () => {
    if (socket && joinedWorkshopId) socket.emit('unsubscribe', joinedWorkshopId)
    joinedWorkshopId = null
    requestedWorkshopId = null
  }

  const syncRoom = () => {
    if (!socket) return

    // Logging out has to drop the connection, or the socket keeps streaming the
    // previous user's workshop to whoever is at the keyboard now.
    if (!userStore.token) {
      leaveCurrentRoom()
      if (socket.connected) socket.disconnect()
      return
    }

    if (!socket.connected) {
      socket.connect()
      return
    }

    const workshopId = currentWorkshopId.value
    if (workshopId === requestedWorkshopId) return

    leaveCurrentRoom()
    if (!workshopId) return

    requestedWorkshopId = workshopId
    socket.emit('subscribe', workshopId, (allowed: boolean) => {
      // A later navigation may have superseded this request while it was away.
      if (requestedWorkshopId !== workshopId) return
      joinedWorkshopId = allowed ? workshopId : null
    })
  }

  // A reconnect starts a fresh session on the server, so the room has to be joined again.
  const rejoinAfterReconnect = () => {
    requestedWorkshopId = null
    joinedWorkshopId = null
    syncRoom()
  }

  socket?.on('connect', rejoinAfterReconnect)

  watch([currentWorkshopId, () => userStore.token], syncRoom, {
    immediate: true
  })

  onUnmounted(() => {
    socket?.off('connect', rejoinAfterReconnect)
    leaveCurrentRoom()
  })
}

export default useWorkshopRoom
